import { DAY_LABELS, isoToHHMM } from 'utils/assist/assistMigrationHours';
import { E164_PHONE_NUMBER_REGEX } from 'constants/assist/phoneNumbers';
import {
  AssistMigrationDistributionPreview,
  AssistMigrationMachineInfo,
  EscalationRule,
  FAQ,
} from 'types/assist/assistMigration';

const collectFaqErrors = (faqs: FAQ[], context: string, errors: string[]) => {
  faqs.forEach((faq, index) => {
    const hasQuestion = !!faq.question.trim();
    const hasAnswer = !!faq.answer.trim();

    if (hasQuestion !== hasAnswer) {
      errors.push(`${context}: FAQ ${index + 1} needs both a question and an answer.`);
    }
  });
};

const collectEscalationErrors = (rules: EscalationRule[], context: string, errors: string[]) => {
  rules.forEach((rule, index) => {
    const hasCondition = !!rule.condition.trim();
    const hasPhone = !!rule.phone.trim();

    if (hasCondition !== hasPhone) {
      errors.push(
        `${context}: escalation rule ${index + 1} needs both a condition and a phone number.`,
      );
    }
  });
};

const collectMachineInfoErrors = (
  machineInfo: AssistMigrationMachineInfo | null,
  context: string,
  errors: string[],
) => {
  if (!machineInfo) return;

  collectFaqErrors(machineInfo.faqs, `${context} FAQs`, errors);
  machineInfo.sizesAndPricing.forEach((sizeAndPricing, index) => {
    const sizeLabel = sizeAndPricing.size.trim() || `row ${index + 1}`;

    if (!sizeAndPricing.size.trim()) {
      errors.push(`${context}: row ${index + 1} needs a machine size.`);
    }
    if (!Number.isFinite(sizeAndPricing.minPrice) || sizeAndPricing.minPrice < 0) {
      errors.push(`${context}: ${sizeLabel} needs a valid minimum price.`);
    }
    if (
      sizeAndPricing.maxPrice !== null &&
      (!Number.isFinite(sizeAndPricing.maxPrice) ||
        sizeAndPricing.maxPrice < sizeAndPricing.minPrice)
    ) {
      errors.push(`${context}: ${sizeLabel} maximum price must be at least the minimum price.`);
    }
  });
};

export const collectDistributionErrors = (
  distribution: AssistMigrationDistributionPreview,
): string[] => {
  const errors: string[] = [];
  const transferNumber = distribution.businessSettings.escalationPhoneNumber?.trim();

  if (transferNumber && transferNumber !== 'N/A' && !E164_PHONE_NUMBER_REGEX.test(transferNumber)) {
    errors.push('Business-wide default transfer number must use +19999999999 or N/A.');
  }

  collectFaqErrors(distribution.global.faqs, 'Global', errors);
  collectEscalationErrors(distribution.global.escalationRules, 'Global', errors);

  distribution.stores.forEach((store) => {
    const storeTransferNumber = store.escalationPhoneNumber?.trim();

    if (
      storeTransferNumber &&
      storeTransferNumber !== 'N/A' &&
      !E164_PHONE_NUMBER_REGEX.test(storeTransferNumber)
    ) {
      errors.push(`${store.storeName}: location transfer number must use +19999999999 or N/A.`);
    }

    collectFaqErrors(store.faqs, `${store.storeName} general FAQs`, errors);
    collectFaqErrors(store.faqsLocationDetails, `${store.storeName} location FAQs`, errors);
    collectFaqErrors(store.faqsHoursOfOperation, `${store.storeName} hours FAQs`, errors);
    collectEscalationErrors(store.escalationRules, store.storeName, errors);
    collectMachineInfoErrors(store.washersInfo, `${store.storeName} washers`, errors);
    collectMachineInfoErrors(store.dryersInfo, `${store.storeName} dryers`, errors);

    store.alternateNames.forEach((alternateName, index) => {
      if (!alternateName.trim()) {
        errors.push(`${store.storeName}: alternate name ${index + 1} cannot be blank.`);
      }
    });

    store.hoursOfOperation.forEach((hours) => {
      if (isoToHHMM(hours.startTime) >= isoToHHMM(hours.endTime)) {
        errors.push(
          `${store.storeName}: ${DAY_LABELS[hours.day]} closing time must be after opening time.`,
        );
      }
    });

    store.serviceCategories.forEach((category, categoryIndex) => {
      const categoryLabel = category.name.trim() || `category ${categoryIndex + 1}`;

      if (!category.name.trim()) {
        errors.push(`${store.storeName}: category ${categoryIndex + 1} needs a name.`);
      }
      (category.services ?? []).forEach((service, serviceIndex) => {
        const serviceLabel = service.name.trim() || `service ${serviceIndex + 1}`;
        const serviceContext = `${store.storeName} › ${categoryLabel}: ${serviceLabel}`;

        if (!service.name.trim()) {
          errors.push(`${serviceContext} needs a name.`);
        }
        if (!Number.isFinite(service.price) || service.price < 0) {
          errors.push(`${serviceContext} needs a valid price.`);
        }
        collectFaqErrors(service.faqs ?? [], `${serviceContext} FAQs`, errors);
      });
    });
  });

  return errors;
};
